import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { formatMinutes } from './timeFormat';
import { stripIdPrefix } from './transactionIdUtils';

// Mirrors the backend InvoiceTemplate defaults so a bill can render before the template loads
export const DEFAULT_INVOICE_TEMPLATE = {
    invoiceTitle: 'Tax Invoice',
    invoicePrefix: 'INV',
    businessName: '',
    businessAddress: '',
    phone: '',
    email: '',
    itemDescription: 'Table usage charges',
    sacCode: '',
    unitLabel: 'Hours',
    chargesLabel: 'Charges',
    gstLabel: 'GST',
    totalLabel: 'Total Amount',
    gstRate: 18,
    splitGst: false,
    notes: '',
    footerText: 'Thank you for visiting!',
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const fmtDateTime = (value) => (value ? moment(value).format('DD MMM YYYY, hh:mm A') : '-');

export const formatAmount = (amount) =>
    round2(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatHours = (minutes) => (round2((Number(minutes) || 0) / 60)).toFixed(2);

// Seller block: each club's own business details, template values as fallback.
// GSTIN is set per club (Venues > Edit Club), not on the template.
const sellerOf = (club, tpl) => {
    const clubBusiness = club?.businessDetails || {};
    return {
        business: {
            name: clubBusiness.name || tpl.businessName || '',
            address: clubBusiness.address || tpl.businessAddress || '',
            phone: clubBusiness.phone || tpl.phone || '',
            email: clubBusiness.email || tpl.email || '',
        },
        gstin: club?.gstNumber || '',
    };
};

// Unit usage (hours) -> charges -> GST -> total
const totalsOf = (lines, tpl) => {
    const totalMinutes = lines.reduce((sum, line) => sum + line.minutes, 0);
    const charges = round2(lines.reduce((sum, line) => sum + line.amount, 0));
    const gstRate = Number(tpl.gstRate) || 0;
    const gstAmount = round2((charges * gstRate) / 100);
    const cgst = round2(gstAmount / 2);
    return {
        totalMinutes,
        unitUsage: formatHours(totalMinutes),
        charges,
        gstRate,
        gstAmount,
        cgst,
        sgst: round2(gstAmount - cgst),
        total: round2(charges + gstAmount),
        inProgress: lines.some((line) => line.inProgress),
    };
};

/**
 * Builds the bill for a table's usage: one line per run, then
 * unit usage (hours) -> charges -> GST -> total.
 * `runs` are the report's run rows ({ startedAt, endedAt, activeMinutes, totalCharge }).
 */
export const buildTableInvoice = ({ table, runs, template, billTo = '', generatedAt = new Date() }) => {
    const tpl = { ...DEFAULT_INVOICE_TEMPLATE, ...(template || {}) };
    const sortedRuns = [...(runs || [])].sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt));

    const lines = sortedRuns.map((run) => ({
        description: `${run.privateBooking ? 'Private booking - ' : ''}${tpl.itemDescription} - ${table.tableName} (${table.tableNumber})`,
        isPrivate: Boolean(run.privateBooking),
        period: `${fmtDateTime(run.startedAt)} to ${run.endedAt ? fmtDateTime(run.endedAt) : 'Running'}`,
        minutes: Number(run.activeMinutes) || 0,
        hours: formatHours(run.activeMinutes),
        amount: round2(run.totalCharge),
        inProgress: !run.endedAt,
    }));

    // Private bookings covered by the invoiced runs, one entry per booking
    const privateBookings = [];
    sortedRuns.forEach((run) => {
        const booking = run.privateBooking;
        if (booking && !privateBookings.some((b) => String(b._id) === String(booking._id))) {
            privateBookings.push({
                _id: booking._id,
                hours: booking.hours,
                price: round2(booking.price),
                period: `${fmtDateTime(booking.startedAt)} to ${fmtDateTime(booking.endedAt || booking.until)}`,
            });
        }
    });

    const firstStart = sortedRuns[0]?.startedAt || generatedAt;
    const prefix = (tpl.invoicePrefix || 'INV').trim();
    const invoiceNumber = `${prefix}-${table.tableNumber}-${moment(firstStart).format('YYYYMMDD-HHmm')}${
        sortedRuns.length > 1 ? `-${sortedRuns.length}` : ''
    }`;

    return {
        template: tpl,
        ...sellerOf(table.club, tpl),
        invoiceNumber,
        invoiceDate: generatedAt,
        billTo: billTo.trim(),
        details: {
            title: 'Table',
            lines: [
                `${table.tableName} (${table.tableNumber}) · ${table.gameType || ''}`,
                [table.club?.name, table.location].filter(Boolean).join(' · '),
            ].filter(Boolean),
        },
        lines,
        emptyMessage: 'No table usage in this period',
        inProgressNote: '* Table is still running; amounts are as of the invoice date.',
        isPrivate: privateBookings.length > 0,
        privateBookings,
        ...totalsOf(lines, tpl),
    };
};

/**
 * Builds the bill for a player's stays: one line per seat session, billed on the
 * session's billable (rounded-up) time. `tables` are the report's table rows, used to
 * resolve each session's club for the seller block and GSTIN.
 */
export const buildPlayerInvoice = ({ player, sessions, tables = [], template, generatedAt = new Date() }) => {
    const tpl = { ...DEFAULT_INVOICE_TEMPLATE, ...(template || {}) };
    const sortedSessions = [...(sessions || [])].sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt));
    const tableById = new Map(tables.map((t) => [String(t._id), t]));

    const lines = sortedSessions.map((session) => ({
        description: `${tpl.itemDescription} - ${session.tableLabel} · Seat ${session.seatNumber}`,
        period: `${fmtDateTime(session.startedAt)} to ${
            session.endedAt ? fmtDateTime(session.endedAt) : 'Playing'
        } · @ Rs. ${formatAmount(session.hourlyRate)}/hr`,
        minutes: Number(session.billableMinutes) || 0,
        hours: formatHours(session.billableMinutes),
        amount: round2(session.charge),
        inProgress: !session.endedAt,
    }));

    // Seller is the club of the first session's table
    const firstTable = tableById.get(String(sortedSessions[0]?.tableId));
    const clubNames = [
        ...new Set(sortedSessions.map((s) => tableById.get(String(s.tableId))?.club?.name).filter(Boolean)),
    ];

    const firstStart = sortedSessions[0]?.startedAt || generatedAt;
    const prefix = (tpl.invoicePrefix || 'INV').trim();
    const playerRef = player.identifier || String(player._id).slice(-6).toUpperCase();
    const invoiceNumber = `${prefix}-${playerRef}-${moment(firstStart).format('YYYYMMDD-HHmm')}${
        sortedSessions.length > 1 ? `-${sortedSessions.length}` : ''
    }`;

    return {
        template: tpl,
        ...sellerOf(firstTable?.club, tpl),
        invoiceNumber,
        invoiceDate: generatedAt,
        billTo: [player.name, player.identifier && `ID: ${player.identifier}`, player.phoneNumber]
            .filter(Boolean)
            .join('\n'),
        details: {
            title: 'Played At',
            lines: [
                clubNames.join(', '),
                [...new Set(sortedSessions.map((s) => s.tableLabel))].join(', '),
            ].filter(Boolean),
        },
        lines,
        emptyMessage: 'No sessions in this period',
        inProgressNote: '* Player is still seated; amounts are as of the invoice date.',
        isPrivate: false,
        privateBookings: [],
        ...totalsOf(lines, tpl),
    };
};

/**
 * Builds the bill for one Add Spend: one line per item (food & drink, table time, ...).
 * `transactions` are the spend's wallet rows (same referenceId). The amounts debited
 * include GST (table time has it added on top of the hourly rate), so each line shows
 * its amount before GST and the GST is listed on top, like the player invoice.
 */
export const buildSpendInvoice = ({ transactions, labelOf, template, generatedAt = new Date() }) => {
    const tpl = { ...DEFAULT_INVOICE_TEMPLATE, ...(template || {}) };
    const first = transactions[0] || {};
    const player = first.userId || {};
    const club = first.clubId && typeof first.clubId === 'object' ? first.clubId : null;

    const gstRate = Number(tpl.gstRate) || 0;
    const beforeGst = (amount) => round2((round2(amount) * 100) / (100 + gstRate));

    const lines = transactions.map((txn) => {
        // Table time paid for a seat stay: name the table, seat and time played
        const bill = txn.tableBill;
        if (bill) {
            // "Table 1 (T-001)"; a name that already says "table" is used as is
            const name = bill.tableName
                ? /table/i.test(bill.tableName) ? bill.tableName : `Table ${bill.tableName}`
                : 'Table';
            const table = `${name}${bill.tableNumber ? ` (${bill.tableNumber})` : ''}`;
            return {
                description: `${table} · Seat ${bill.seatNumber}`,
                period:
                    `${fmtDateTime(bill.startedAt)} to ${bill.endedAt ? fmtDateTime(bill.endedAt) : 'Playing'}` +
                    ` · ${formatMinutes(bill.billableMinutes)} @ Rs. ${formatAmount(bill.hourlyRate)}/hr` +
                    (bill.gstAmount > 0 ? ` + ${bill.gstRate}% GST` : ''),
                minutes: Number(bill.billableMinutes) || 0,
                hours: formatHours(bill.billableMinutes),
                amount: beforeGst(txn.amount),
                paid: round2(txn.amount),
                inProgress: false,
            };
        }
        return {
            description: [labelOf(txn), txn.adminNotes].filter(Boolean).join(' - '),
            period: fmtDateTime(txn.createdAt),
            minutes: 0,
            hours: '-',
            amount: beforeGst(txn.amount),
            paid: round2(txn.amount),
            inProgress: false,
        };
    });

    // Total is what was debited; GST is the rest once the line charges are taken off
    const total = round2(lines.reduce((sum, line) => sum + line.paid, 0));
    const charges = round2(lines.reduce((sum, line) => sum + line.amount, 0));
    const gstAmount = round2(total - charges);
    const cgst = round2(gstAmount / 2);

    const prefix = (tpl.invoicePrefix || 'INV').trim();
    return {
        template: tpl,
        ...sellerOf(club, tpl),
        invoiceNumber: `${prefix}-${stripIdPrefix(first.referenceId || first.transactionId)}`,
        invoiceDate: first.createdAt || generatedAt,
        billTo: [player.name, player.identifier && `ID: ${player.identifier}`, player.phoneNumber]
            .filter(Boolean)
            .join('\n'),
        details: {
            title: 'Paid From',
            lines: ['Player wallet', club ? `${club.clubCode ? `${club.clubCode} - ` : ''}${club.name}` : first.clubName]
                .filter(Boolean),
        },
        lines,
        emptyMessage: 'No items',
        inProgressNote: '',
        isPrivate: false,
        privateBookings: [],
        hideUsage: true,
        gstInclusive: false,
        totalMinutes: 0,
        unitUsage: '-',
        charges,
        gstRate,
        gstAmount,
        cgst,
        sgst: round2(gstAmount - cgst),
        total,
        inProgress: false,
    };
};

// GST rows for the totals block, honouring the CGST/SGST split setting
export const gstRowsOf = (invoice) => {
    const { template: tpl, gstRate, gstAmount, cgst, sgst } = invoice;
    if (tpl.splitGst) {
        const half = round2(gstRate / 2);
        return [
            { label: `CGST @ ${half}%`, amount: cgst },
            { label: `SGST @ ${half}%`, amount: sgst },
        ];
    }
    return [{ label: `${tpl.gstLabel || 'GST'} @ ${gstRate}%`, amount: gstAmount }];
};

// jsPDF's built-in fonts have no rupee glyph, so the PDF uses "Rs."
const pdfAmount = (amount) => `Rs. ${formatAmount(amount)}`;

const createInvoicePDF = (invoice) => {
    const tpl = invoice.template;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const right = pageWidth - 14;
    let y = 18;

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(invoice.business.name || 'Invoice', 14, y);
    doc.setFontSize(14);
    doc.text(tpl.invoiceTitle || 'Invoice', right, y, { align: 'right' });

    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90);
    const businessLines = [
        ...(invoice.business.address ? doc.splitTextToSize(invoice.business.address, 110) : []),
        invoice.gstin ? `GSTIN: ${invoice.gstin}` : null,
        [invoice.business.phone, invoice.business.email].filter(Boolean).join(' | ') || null,
    ].filter(Boolean);
    businessLines.forEach((line, idx) => doc.text(line, 14, y + 6 + idx * 4.5));

    const metaLines = [
        `Invoice No: ${invoice.invoiceNumber}`,
        `Date: ${moment(invoice.invoiceDate).format('DD MMM YYYY, hh:mm A')}`,
        ...(invoice.isPrivate ? ['PRIVATE TABLE BOOKING'] : []),
    ];
    metaLines.forEach((line, idx) => {
        const isBadge = invoice.isPrivate && idx === metaLines.length - 1;
        if (isBadge) {
            doc.setFont(undefined, 'bold');
            doc.setTextColor(234, 88, 12);
        }
        doc.text(line, right, y + 6 + idx * 4.5, { align: 'right' });
        if (isBadge) {
            doc.setFont(undefined, 'normal');
            doc.setTextColor(90);
        }
    });

    y += 8 + Math.max(businessLines.length, metaLines.length) * 4.5 + 4;
    doc.setDrawColor(200);
    doc.line(14, y, right, y);
    y += 7;

    doc.setTextColor(40);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Bill To', 14, y);
    doc.text(invoice.details.title, pageWidth / 2, y);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    const billToLines = (invoice.billTo || '-').split('\n');
    billToLines.forEach((line, idx) => doc.text(line, 14, y + 5 + idx * 4.5));
    const detailLines = invoice.details.lines.flatMap((line) =>
        doc.splitTextToSize(line.replace(/ · /g, ' - '), pageWidth / 2 - 14)
    );
    detailLines.forEach((line, idx) => doc.text(line, pageWidth / 2, y + 5 + idx * 4.5));
    y += 5 + Math.max(billToLines.length, detailLines.length) * 4.5 + 4;

    if (invoice.isPrivate) {
        doc.setFont(undefined, 'bold');
        doc.setFontSize(10);
        doc.setTextColor(234, 88, 12);
        doc.text('Private Table Booking', 14, y);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.setTextColor(40);
        invoice.privateBookings.forEach((booking, idx) => {
            doc.text(
                `${booking.period} - ${booking.hours} hr - Booking price ${pdfAmount(booking.price)}`,
                14,
                y + 5 + idx * 4.5
            );
        });
        y += 5 + invoice.privateBookings.length * 4.5 + 4;
    }

    const withSac = Boolean(tpl.sacCode);
    const withUsage = !invoice.hideUsage;
    const numericCol = withSac ? 3 : 2;
    doc.autoTable({
        startY: y,
        head: [
            [
                '#',
                'Description',
                ...(withSac ? ['SAC'] : []),
                ...(withUsage ? [`Usage (${tpl.unitLabel})`] : []),
                'Amount',
            ],
        ],
        body: invoice.lines.map((line, idx) => [
            idx + 1,
            `${line.description}\n${line.period}`.replace(/ · /g, ' - '),
            ...(withSac ? [tpl.sacCode] : []),
            ...(withUsage ? [line.hours] : []),
            pdfAmount(line.amount),
        ]),
        theme: 'grid',
        styles: { fontSize: 8.5, cellPadding: 2 },
        headStyles: { fillColor: [55, 65, 81] },
        columnStyles: {
            0: { cellWidth: 10 },
            ...(withSac ? { 2: { cellWidth: 18 } } : {}),
            ...(withUsage
                ? {
                      [numericCol]: { halign: 'right', cellWidth: 28 },
                      [numericCol + 1]: { halign: 'right', cellWidth: 32 },
                  }
                : { [numericCol]: { halign: 'right', cellWidth: 32 } }),
        },
    });

    const totals = [
        ...(withUsage ? [[`Unit Usage (${tpl.unitLabel})`, invoice.unitUsage]] : []),
        [invoice.gstInclusive ? 'Taxable value' : tpl.chargesLabel || 'Charges', pdfAmount(invoice.charges)],
        ...gstRowsOf(invoice).map((row) => [row.label, pdfAmount(row.amount)]),
        [tpl.totalLabel || 'Total Amount', pdfAmount(invoice.total)],
    ];
    doc.autoTable({
        startY: doc.lastAutoTable.finalY + 4,
        body: totals,
        theme: 'plain',
        margin: { left: pageWidth / 2 },
        styles: { fontSize: 9.5, cellPadding: 1.8 },
        columnStyles: { 1: { halign: 'right' } },
        didParseCell: (data) => {
            if (data.row.index === totals.length - 1) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [243, 244, 246];
            }
        },
    });

    y = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8.5);
    doc.setTextColor(90);
    if (invoice.inProgress) {
        doc.text(invoice.inProgressNote, 14, y);
        y += 5;
    }
    if (invoice.gstInclusive) {
        doc.text('* Amounts are inclusive of GST and were paid from the player wallet.', 14, y);
        y += 5;
    }
    if (tpl.notes) {
        doc.splitTextToSize(tpl.notes, right - 14).forEach((line) => {
            doc.text(line, 14, y);
            y += 4.5;
        });
    }
    if (tpl.footerText) {
        doc.text(tpl.footerText, pageWidth / 2, Math.max(y + 6, 280), { align: 'center' });
    }

    return doc;
};

export const downloadInvoicePDF = (invoice) => {
    createInvoicePDF(invoice).save(`${invoice.invoiceNumber}.pdf`);
};

export const printInvoicePDF = (invoice) => {
    const doc = createInvoicePDF(invoice);
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
};
